import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChartBarIcon, CheckIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { siteStatsApi } from "../api/cmsApi";
import toast from "react-hot-toast";

interface SiteStats {
  totalPaidOut: number;
  claimsProcessed: number;
  averagePayout: number;
  successRate: number;
  processingTimeDays: number;
}

const DEFAULT_STATS: SiteStats = {
  totalPaidOut: 1247832,
  claimsProcessed: 4823,
  averagePayout: 347,
  successRate: 94,
  processingTimeDays: 3,
};

export default function SiteStats() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<SiteStats>(DEFAULT_STATS);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: stats, isLoading } = useQuery({
    queryKey: ["site-stats"],
    queryFn: () => siteStatsApi.get().then((res) => res.data.data as SiteStats | null),
  });

  // Update form when data loads
  useEffect(() => {
    if (stats) {
      setFormData(stats);
      setHasChanges(false);
    }
  }, [stats]);

  const updateMutation = useMutation({
    mutationFn: (data: SiteStats) => siteStatsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-stats"] });
      setHasChanges(false);
      toast.success("Statistiken gespeichert");
    },
    onError: () => toast.error("Fehler beim Speichern"),
  });

  const handleChange = (field: keyof SiteStats, value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData((prev) => ({ ...prev, [field]: numValue }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Only send the expected fields, exclude _id from the payload
    const { totalPaidOut, claimsProcessed, averagePayout, successRate, processingTimeDays } = formData;
    updateMutation.mutate({ totalPaidOut, claimsProcessed, averagePayout, successRate, processingTimeDays });
  };

  const handleReset = () => {
    if (stats) {
      setFormData(stats);
    } else {
      setFormData(DEFAULT_STATS);
    }
    setHasChanges(false);
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString("de-DE");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="h-8 w-8 text-gray-700 dark:text-gray-300" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Site Statistiken</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Verwalte die auf der Website angezeigten Statistiken
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Zuruecksetzen
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckIcon className="h-4 w-4 mr-2" />
            {updateMutation.isPending ? "Speichern..." : "Speichern"}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-12 text-center">
          <div className="text-gray-500 dark:text-gray-400">Laden...</div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Oeffentliche Statistiken
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Diese Werte werden auf der Landing Page und an anderen Stellen der Website angezeigt.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Total Paid Out */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Gesamtauszahlung (EUR)
                </label>
                <input
                  type="number"
                  value={formData.totalPaidOut}
                  onChange={(e) => handleChange("totalPaidOut", e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Angezeigt: {formatNumber(formData.totalPaidOut)} EUR
                </p>
              </div>

              {/* Claims Processed */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Bearbeitete Ansprueche
                </label>
                <input
                  type="number"
                  value={formData.claimsProcessed}
                  onChange={(e) => handleChange("claimsProcessed", e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Angezeigt: {formatNumber(formData.claimsProcessed)}
                </p>
              </div>

              {/* Average Payout */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Durchschnittliche Auszahlung (EUR)
                </label>
                <input
                  type="number"
                  value={formData.averagePayout}
                  onChange={(e) => handleChange("averagePayout", e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Angezeigt: {formatNumber(formData.averagePayout)} EUR
                </p>
              </div>

              {/* Success Rate */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Erfolgsquote (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.successRate}
                  onChange={(e) => handleChange("successRate", e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Angezeigt: {formData.successRate}%
                </p>
              </div>

              {/* Processing Time */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Bearbeitungszeit (Tage)
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.processingTimeDays}
                  onChange={(e) => handleChange("processingTimeDays", e.target.value)}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Angezeigt: {formData.processingTimeDays} Tage
                </p>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-lg">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
              Vorschau (wie auf der Website angezeigt)
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(formData.totalPaidOut)} EUR
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Gesamtauszahlung
                </div>
              </div>
              <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(formData.claimsProcessed)}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Ansprueche bearbeitet
                </div>
              </div>
              <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {formatNumber(formData.averagePayout)} EUR
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Durchschn. Auszahlung
                </div>
              </div>
              <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formData.successRate}%
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Erfolgsquote
                </div>
              </div>
              <div className="text-center p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                  {formData.processingTimeDays} Tage
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Bearbeitungszeit
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
          Hinweis
        </h4>
        <p className="text-sm text-blue-700 dark:text-blue-400">
          Diese Statistiken werden auf der Landing Page im Hero-Bereich, in der Trust-Sektion
          und an anderen Stellen angezeigt. Aenderungen werden sofort live geschaltet.
        </p>
      </div>
    </div>
  );
}
